import React, { useState, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  useSearchParams,
  useLocation,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import HomePage from './pages/HomePage';
import ProductPage from './pages/ProductPage';
import ProductPageMobile from './pages/ProductPageMobile';
import SellerDashboard from './pages/SellerDashboard';
import AdminDashboard from './pages/AdminDashboard';
import CollectorDashboard from './pages/CollectorDashboard';
import CartPageDesktop from './pages/CartPageDesktop';
import CartPageMobile from './pages/CartPageMobile';
import CheckoutPageDesktop from './pages/CheckoutPageDesktop';
import CheckoutPageMobile from './pages/CheckoutPageMobile';
import ProfilePageDesktop from './pages/ProfilePageDesktop';
import ProfilePageMobile from './pages/ProfilePageMobile';
import FavoritesPage from './pages/FavoritesPage';
import SellerShopPage from './pages/SellerShopPage';
import CategoriesPageMobile from './pages/CategoriesPageMobile';
import CustomerOrdersPage from './pages/CustomerOrdersPage';
import OrderDetailsPage from './pages/OrderDetailsPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import StoreInfoPage from './pages/StoreInfoPage';
import Sidebar from './components/Sidebar';
import Footer from './components/Footer';
import Icon from './components/Icon';
import { CartProvider, useCart } from './context/CartContext';
import { FavoriteProvider, useFavorites } from './context/FavoriteContext';
import api from './api';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

function Header({ isMobile }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [localSearch, setLocalSearch] = useState(
    searchParams.get('search') || ''
  );
  const { totalCount } = useCart();
  const { items: favItems } = useFavorites();
  useEffect(
    () => setLocalSearch(searchParams.get('search') || ''),
    [searchParams]
  );
  const handleSearch = (event) => {
    event.preventDefault();
    const params = new URLSearchParams();
    if (localSearch.trim()) params.set('search', localSearch.trim());
    navigate(`/?${params.toString()}`);
  };
  return (
    <header className="market-header">
      <div className="market-topline">
        <span>Одежда, обувь и детали вашего стиля</span>
        <a href="/seller">
          Продавать на MaxMarket <Icon name="arrow" size={14} />
        </a>
      </div>
      <div className="market-header-main">
        <a
          href="/"
          className="market-brand"
          aria-label="MaxMarket — на главную"
        >
          <span className="brand-mark">
            <Icon name="bag" size={23} />
          </span>
          max<span>market</span>
          <i />
        </a>
        <a className="market-catalog-button" href="/categories">
          <Icon name="grid" />
          Каталог
        </a>
        <form className="market-search" role="search" onSubmit={handleSearch}>
          <input
            aria-label="Поиск товаров"
            type="search"
            placeholder="Найти на MaxMarket"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
          />
          <button type="submit" aria-label="Найти товары">
            <Icon name="search" size={22} />
          </button>
        </form>
        <nav className="market-user-nav" aria-label="Личный кабинет">
          <a href="/orders">
            <Icon name="box" />
            <span>Заказы</span>
          </a>
          <a href="/favorites">
            <Icon name="heart" />
            <span>Избранное</span>
            {favItems.length > 0 && <b>{favItems.length}</b>}
          </a>
          <a href="/profile">
            <Icon name="user" />
            <span>Профиль</span>
          </a>
          <a href="/cart">
            <Icon name="bag" />
            <span>Корзина</span>
            {totalCount > 0 && <b>{totalCount}</b>}
          </a>
        </nav>
        {isMobile && (
          <a
            className="market-mobile-filter"
            href="/categories"
            aria-label="Открыть категории"
          >
            <Icon name="grid" />
          </a>
        )}
      </div>
      <nav className="market-category-nav" aria-label="Категории товаров">
        <a
          href="/"
          className={
            !searchParams.get('category') && !searchParams.get('search')
              ? 'active'
              : ''
          }
        >
          Весь каталог
        </a>
        {['одежда', 'обувь', 'аксессуары'].map((category) => (
          <a
            key={category}
            href={`/?category=${category}`}
            className={
              searchParams.get('category') === category ? 'active' : ''
            }
          >
            {category}
          </a>
        ))}
        <span>Найдётся то самое.</span>
      </nav>
    </header>
  );
}

function MobileBottomNav() {
  const location = useLocation();
  const { totalCount } = useCart();

  if (['/seller', '/admin', '/collector'].includes(location.pathname))
    return null;
  const isActive = (path) =>
    location.pathname === path ? 'bottom-nav-item active' : 'bottom-nav-item';

  return (
    <nav className="mobile-bottom-nav" aria-label="Основная навигация">
      <a href="/" className={isActive('/')}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          width="24"
          height="24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6h16M4 10h16M4 14h16M4 18h16"
          />
        </svg>
        <span>Витрина</span>
      </a>
      <a href="/categories" className={isActive('/categories')}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          width="24"
          height="24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
          />
        </svg>
        <span>Категории</span>
      </a>
      <a href="/cart" className={isActive('/cart')}>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            width="24"
            height="24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
          {totalCount > 0 && (
            <span
              className="cart-badge-blue"
              style={{ position: 'absolute', top: -8, right: -12 }}
            >
              {totalCount}
            </span>
          )}
        </div>
        <span>Корзина</span>
      </a>
      <a href="/profile" className={isActive('/profile')}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          width="24"
          height="24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
        <span>Профиль</span>
      </a>
    </nav>
  );
}

function getWindowWidth() {
  return typeof window !== 'undefined' ? window.innerWidth : 1024;
}

function App() {
  const [filterOpen, setFilterOpen] = useState(false);
  const [width, setWidth] = useState(getWindowWidth);

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = width <= 768;

  useEffect(() => {
    const customer = localStorage.getItem('customer');
    if (customer && localStorage.getItem('customerAccessToken')) {
      try {
        const parsed = JSON.parse(customer);
        if (parsed.phone) {
          api
            .get('/api/auth/profile')
            .then((res) => {
              if (res.data.phone !== parsed.phone || JSON.parse(localStorage.getItem('customer') || '{}').phone !== parsed.phone) return;
              const { cart, favorites, savedPoints } = res.data;
              if (cart) {
                try {
                  const cartData = JSON.parse(cart);
                  window.__cartDispatch &&
                    window.__cartDispatch({
                      type: 'HYDRATE_CART',
                      payload: cartData,
                    });
                } catch (e) {}
              }
              if (favorites) {
                try {
                  const favData = JSON.parse(favorites);
                  window.__favDispatch &&
                    window.__favDispatch({
                      type: 'HYDRATE_FAVORITES',
                      payload: favData,
                    });
                } catch (e) {}
              }
              if (savedPoints) {
                const updatedCustomer = { ...parsed, savedPoints };
                localStorage.setItem(
                  'customer',
                  JSON.stringify(updatedCustomer)
                );
              }
            })
            .catch((err) =>
              console.error('Ошибка загрузки профиля при старте', err)
            );
        }
      } catch (e) {}
    }
  }, []);

  const CartComponent = isMobile ? CartPageMobile : CartPageDesktop;
  const CheckoutComponent = isMobile ? CheckoutPageMobile : CheckoutPageDesktop;
  const ProfileComponent = isMobile ? ProfilePageMobile : ProfilePageDesktop;
  const ProductComponent = isMobile ? ProductPageMobile : ProductPage;

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <CartProvider>
          <FavoriteProvider>
            <Routes>
              {/* Маршруты без общего лейаута */}
              <Route path="/seller" element={<SellerDashboard />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/collector" element={<CollectorDashboard />} />

              {/* Все остальные маршруты внутри общего лейаута */}
              <Route
                path="*"
                element={
                  <AppInner
                    filterOpen={filterOpen}
                    setFilterOpen={setFilterOpen}
                    isMobile={isMobile}
                    CartComponent={CartComponent}
                    CheckoutComponent={CheckoutComponent}
                    ProfileComponent={ProfileComponent}
                    ProductComponent={ProductComponent}
                  />
                }
              />
            </Routes>
            {isMobile && <MobileBottomNav />}
          </FavoriteProvider>
        </CartProvider>
      </Router>
    </QueryClientProvider>
  );
}

function AppInner({
  filterOpen,
  setFilterOpen,
  isMobile,
  CartComponent,
  CheckoutComponent,
  ProfileComponent,
  ProductComponent,
}) {
  const { dispatch: cartDispatch } = useCart();
  const { dispatch: favDispatch } = useFavorites();

  useEffect(() => {
    window.__cartDispatch = cartDispatch;
    window.__favDispatch = favDispatch;
  }, [cartDispatch, favDispatch]);

  const location = useLocation();
  const hideHeader = isMobile && location.pathname.startsWith('/product');
  const isCatalog = location.pathname === '/';
  useEffect(() => setFilterOpen(false), [location.pathname, setFilterOpen]);
  const closeFilters = () => {
    setFilterOpen(false);
    document.querySelector('.catalog-filter-trigger')?.focus();
  };

  return (
    <>
      {!hideHeader && (
        <Header
          onFilterToggle={() => setFilterOpen(!filterOpen)}
          isMobile={isMobile}
        />
      )}
      {process.env.REACT_APP_BANK_REVIEW_MODE === 'true' && (
        <div role="note" className="store-preparing-notice">
          {process.env.REACT_APP_CUSTOMER_CHECKOUT_ENABLED === 'true'
            ? 'Каталог демонстрационный. Оплата через рабочий терминал доступна после входа в профиль.'
            : 'MaxMarket готовится к открытию. Каталог демонстрационный. Приём заказов и оплата пока недоступны.'}
        </div>
      )}
      <a className="skip-link" href="#main-content">
        Перейти к содержимому
      </a>
      <div
        className={`main-container market-layout ${isCatalog ? 'market-layout--catalog' : ''}`}
      >
        {isCatalog && <Sidebar isOpen={filterOpen} onClose={closeFilters} />}
        <main id="main-content" className="content-area" style={{ flex: 1 }}>
          {location.state?.notice && <p className="buyer-notice" role="status">{location.state.notice}</p>}
          <Routes>
            <Route
              path="/"
              element={
                <HomePage
                  filterOpen={filterOpen}
                  onFilterToggle={() => setFilterOpen((open) => !open)}
                />
              }
            />
            <Route path="/categories" element={<CategoriesPageMobile />} />
            {['contacts', 'delivery', 'returns', 'privacy', 'offer'].map(section => (
              <Route key={section} path={`/${section}`} element={<StoreInfoPage section={section} />} />
            ))}
            <Route path="/product/:id" element={<ProductComponent />} />
            <Route path="/shop/:id" element={<SellerShopPage />} />
            <Route path="/cart" element={<CartComponent />} />
            <Route path="/checkout" element={<CheckoutComponent />} />
            <Route path="/checkout/success" element={<PaymentSuccessPage />} />
            <Route path="/payment-success" element={<PaymentSuccessPage />} />
            <Route path="/orders" element={<CustomerOrdersPage />} />
            <Route path="/orders/:id" element={<OrderDetailsPage />} />
            <Route path="/favorites" element={<FavoritesPage />} />
            <Route path="/profile" element={<ProfileComponent />} />
          </Routes>
        </main>
      </div>
      <Footer />
    </>
  );
}

export default App;
